import {
  Button as ChakraButton,
  ButtonProps as ChakraButtonProps,
  SpaceProps,
} from "@chakra-ui/react";
import { forwardRef, useMemo } from "react";
import { isBrandLook } from "theme/look";

export type ButtonVariant =
  | "primary"
  | "primary-teal"
  | "secondary"
  | "outline"
  | "success"
  | "ghost"
  | "warning"
  | "link"
  | "outline-teal"
  | "link-teal"
  | "chip";

type ButtonProps = {
  children: ChakraButtonProps["children"];
  variant?: ButtonVariant;
  fullWidth?: boolean;
  onClick?: ChakraButtonProps["onClick"];
  rightIcon?: ChakraButtonProps["rightIcon"];
  leftIcon?: ChakraButtonProps["leftIcon"];
  justifyContent?: ChakraButtonProps["justifyContent"];
  pointerEvents?: ChakraButtonProps["pointerEvents"];
  h?: ChakraButtonProps["h"];
  w?: ChakraButtonProps["w"];
  size?: "md" | "sm" | "xs";
  type?: ChakraButtonProps["type"];
  isDisabled?: ChakraButtonProps["isDisabled"];
  isActive?: ChakraButtonProps["isActive"];
  isLoading?: ChakraButtonProps["isLoading"];
  as?: ChakraButtonProps["as"];
  download?: string;
  href?: string;
} & SpaceProps;

const getPropsPerVariant = (variant: ButtonVariant): ChakraButtonProps => {
  switch (variant) {
    case "primary":
    case "primary-teal":
      return {
        variant: "solid",
        colorScheme: "teal",
      };

    case "secondary":
      return {
        variant: "solid",
        colorScheme: "gray",
      };

    case "outline":
      return {
        variant: "outline",
        colorScheme: "gray",
      };

    case "success":
      return {
        variant: "solid",
        colorScheme: "green",
      };

    case "warning":
      return {
        variant: "solid",
        colorScheme: "red",
      };

    case "ghost":
      return {
        variant: "ghost",
        colorScheme: "gray",
      };

    case "outline-teal":
      return {
        variant: "outline",
        colorScheme: "teal",
        color: "gray.900",
        borderColor: "teal.500",
        _dark: {
          color: "white",
        },
        fontWeight: "400",
      };

    case "link":
      return {
        variant: "link",
        colorScheme: "black",
        textDecoration: "underline",
        fontWeight: "bold",
      };

    case "chip":
      return { variant: "chip" };

    case "link-teal":
      return {
        variant: "link",
        colorScheme: "teal",
        textDecoration: "none",
        fontWeight: "bold",
      };
  }
};

const legacyOutlineTeal: ChakraButtonProps = {
  variant: "outline",
  colorScheme: "teal",
  color: "brand.900",
  borderColor: "teal.500",
  _dark: {
    color: "white",
  },
  fontWeight: "400",
};

/** Pre-restyle variants; `chip` maps to the teal toggles it replaced. */
const getLegacyPropsPerVariant = (
  variant: ButtonVariant,
  isActive: boolean | undefined,
): ChakraButtonProps => {
  switch (variant) {
    case "primary":
      return { variant: "solid", colorScheme: "yellow" };

    case "primary-teal":
      return { variant: "solid", colorScheme: "teal", color: "black" };

    case "outline-teal":
      return legacyOutlineTeal;

    case "chip":
      return isActive
        ? { variant: "solid", colorScheme: "teal", color: "black" }
        : legacyOutlineTeal;

    default:
      return getPropsPerVariant(variant);
  }
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      isDisabled,
      isActive,
      isLoading,
      type,
      children,
      onClick,
      rightIcon,
      leftIcon,
      justifyContent,
      h,
      w,
      pointerEvents,
      size = "sm",
      variant = "secondary",
      fullWidth = false,
      as,
      download,
      href,
      ...spaceProps
    },
    ref
  ) => {
    const variantProps = useMemo(() => {
      return isBrandLook
        ? getPropsPerVariant(variant)
        : getLegacyPropsPerVariant(variant, isActive);
    }, [variant, isActive]);

    return (
      <ChakraButton
        ref={ref}
        w={fullWidth ? "100%" : w}
        h={h}
        isDisabled={isLoading || isDisabled}
        // Legacy chips show state through their variant, not `isActive`.
        isActive={!isBrandLook && variant === "chip" ? undefined : isActive}
        isLoading={isLoading}
        type={type}
        fontWeight={isBrandLook ? "medium" : "semibold"}
        onClick={onClick}
        size={size}
        rightIcon={rightIcon}
        leftIcon={leftIcon}
        justifyContent={justifyContent}
        pointerEvents={pointerEvents}
        {...variantProps}
        {...spaceProps}
        as={as}
        download={download}
        href={href}
      >
        {children}
      </ChakraButton>
    );
  }
);

export { Button };
